/**
 * Hibaodalak — 404, 403, 500, offline, stb.
 */
import { useNavigate } from 'react-router-dom';

interface Props {
  code?: number | string;
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
}

// ── SVG ikonok kódtól függően ────────────────────────────────────────────────

function Icon404() {
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Nagyító */}
      <circle cx="34" cy="34" r="18" stroke="#14233F" strokeWidth="5" strokeLinecap="round"/>
      <line x1="47" y1="47" x2="64" y2="64" stroke="#F47B20" strokeWidth="5" strokeLinecap="round"/>
      {/* Kérdőjel */}
      <text x="34" y="40" textAnchor="middle" fontSize="18" fontWeight="800" fill="#14233F" fontFamily="system-ui">?</text>
    </svg>
  );
}

function Icon403() {
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Lakat test */}
      <rect x="18" y="38" width="44" height="30" rx="7" fill="#14233F"/>
      {/* Lakat ív */}
      <path d="M26 38 V28 a14 14 0 0 1 28 0 V38" stroke="#14233F" strokeWidth="5" strokeLinecap="round" fill="none"/>
      {/* Kulcslyuk */}
      <circle cx="40" cy="52" r="5" fill="#F47B20"/>
      <rect x="38" y="54" width="4" height="7" rx="2" fill="#F47B20"/>
    </svg>
  );
}

function Icon500() {
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Villám */}
      <polygon points="44,8 22,44 38,44 36,72 58,36 42,36" fill="#F47B20" stroke="#F47B20" strokeWidth="2" strokeLinejoin="round"/>
      {/* Crack vonal */}
      <line x1="12" y1="20" x2="20" y2="28" stroke="#14233F" strokeWidth="3" strokeLinecap="round" opacity="0.3"/>
      <line x1="62" y1="50" x2="68" y2="58" stroke="#14233F" strokeWidth="3" strokeLinecap="round" opacity="0.3"/>
    </svg>
  );
}

function IconOffline() {
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Wifi ívek (halvány) */}
      <path d="M14 36 Q40 12 66 36" stroke="#14233F" strokeWidth="5" strokeLinecap="round" fill="none" opacity="0.2"/>
      <path d="M22 46 Q40 28 58 46" stroke="#14233F" strokeWidth="5" strokeLinecap="round" fill="none" opacity="0.2"/>
      {/* Pont */}
      <circle cx="40" cy="60" r="5" fill="#14233F" opacity="0.2"/>
      {/* Áthúzó X */}
      <line x1="18" y1="18" x2="62" y2="62" stroke="#F47B20" strokeWidth="5" strokeLinecap="round"/>
      <line x1="62" y1="18" x2="18" y2="62" stroke="#F47B20" strokeWidth="5" strokeLinecap="round"/>
    </svg>
  );
}

function IconGeneric() {
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Háromszög */}
      <path d="M40 12 L70 64 H10 Z" stroke="#14233F" strokeWidth="5" strokeLinejoin="round" fill="none"/>
      {/* Felkiáltójel */}
      <line x1="40" y1="30" x2="40" y2="50" stroke="#F47B20" strokeWidth="5" strokeLinecap="round"/>
      <circle cx="40" cy="58" r="3" fill="#F47B20"/>
    </svg>
  );
}

function getIcon(code?: number | string) {
  const c = Number(code);
  if (c === 404) return <Icon404 />;
  if (c === 403) return <Icon403 />;
  if (c === 500 || c === 503) return <Icon500 />;
  if (code === 'offline') return <IconOffline />;
  return <IconGeneric />;
}

// ── Fő komponens ─────────────────────────────────────────────────────────────

export default function ErrorPage({ code, title, description, action }: Props) {
  const navigate = useNavigate();

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 20px',
      background: 'var(--bg, #F4F5F9)',
      fontFamily: "'Hanken Grotesk', sans-serif",
      textAlign: 'center',
    }}>
      {/* Logo */}
      <div style={{ marginBottom: '32px', opacity: 0.6 }}>
        <svg width="32" height="32" viewBox="0 0 100 100">
          <path d="M16 88 L16 40 L50 64" fill="none" stroke="#14233F" strokeWidth="17" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M50 64 L84 16 L84 64" fill="none" stroke="#F47B20" strokeWidth="17" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {/* Ikon */}
      <div style={{ marginBottom: '24px' }}>
        {getIcon(code)}
      </div>

      {/* Kód */}
      {code && (
        <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--slate, #5A6680)', marginBottom: '8px' }}>
          {code}
        </div>
      )}

      {/* Cím */}
      <h1 style={{ fontSize: '22px', fontWeight: 800, color: 'var(--navy, #14233F)', margin: '0 0 10px', lineHeight: 1.2 }}>
        {title}
      </h1>

      {/* Leírás */}
      {description && (
        <p style={{ fontSize: '14px', color: 'var(--slate, #5A6680)', margin: '0 0 28px', maxWidth: '360px', lineHeight: 1.6 }}>
          {description}
        </p>
      )}

      {/* Gombok */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {action && (
          <button onClick={action.onClick} style={btnPrimary}>
            {action.label}
          </button>
        )}
        <button onClick={() => window.location.href = 'https://crm.modulum.hu'} style={btnPrimary}>
          CRM megnyitása
        </button>
        <button onClick={() => window.location.href = 'https://modulum.hu'} style={btnGhost}>
          Főoldal
        </button>
      </div>
    </div>
  );
}

// ── Előre gyártott oldalak ────────────────────────────────────────────────────

export function NotFoundPage() {
  return (
    <ErrorPage
      code={404}
      title="Az oldal nem található"
      description="A keresett oldal nem létezik, vagy áthelyezték. Ellenőrizd a címet, vagy navigálj vissza."
    />
  );
}

export function ForbiddenPage() {
  return (
    <ErrorPage
      code={403}
      title="Hozzáférés megtagadva"
      description="Nincs jogosultságod ehhez az oldalhoz. Ha úgy gondolod, hiba történt, keresd a rendszergazdát."
    />
  );
}

export function ServerErrorPage() {
  return (
    <ErrorPage
      code={500}
      title="Szerverhiba"
      description="Valami nem várt hiba történt a szerveren. Próbáld újra pár másodperc múlva."
      action={{ label: 'Újrapróbálás', onClick: () => window.location.reload() }}
    />
  );
}

export function OfflinePage() {
  return (
    <ErrorPage
      code="offline"
      title="Nincs internetkapcsolat"
      description="Úgy tűnik, megszakadt a kapcsolat. Ellenőrizd a hálózatot, majd próbálj újra."
      action={{ label: 'Újrapróbálás', onClick: () => window.location.reload() }}
    />
  );
}

// ── Stílusok ─────────────────────────────────────────────────────────────────

const btnPrimary: React.CSSProperties = {
  background: '#F47B20',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  padding: '10px 20px',
  fontSize: '13px',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const btnGhost: React.CSSProperties = {
  background: 'transparent',
  color: '#14233F',
  border: '1.5px solid #D1D5E0',
  borderRadius: '8px',
  padding: '9px 18px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
